import { useState } from 'react';
import { Button, Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../state/store';
import Icon from '../../common/components/Icon/Icon';
import { LrvForm } from '../../common/components/LrvForm/LrvForm';
import { useFormErrorFields } from '../../hooks/useFormErrorFields';
import { LrvModal } from '../../common/components/LrvModal/LrvModal';
import { LrvSwitch } from '../../common/components/LrvSwitch/LrvSwitch';
import { LrvInputV2 } from '../../common/components/LrvInputV2/LrvInputV2';
import { LrvCheckbox } from '../../common/components/LrvCheckbox/LrvCheckbox';
import { LrvSelectV2 } from '../../common/components/LrvSelectV2/LrvSelectV2';
import PasswordPolicy from '../../common/components/LrvPopover/PasswordPolicy';
import { LrvPasswordV2 } from '../../common/components/LrvPasswordV2/LrvPasswordV2';
import { isFieldsTouchedCustom, validatePasswordPolicy } from '../../utils/validations';
import { isPromoterRole, isSalesRole, isSuperAdmin, LARVIA_ID, roles, PATTERN, isBusinessManagerRole, isOwnerRole, isDistributorCompany, isSalesManagerRole } from '../../config/commons';

import styles from './Users.module.scss';
import * as usersSlice from './usersSlice';
import { getValueActiveUsers } from './helpers';
import { Campus, CreateUser } from './interfaces';

const { Option } = Select;
const ALL_UNITS = 'ALL_UNITS';

export const NewUserForm = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const { errorFields, extractAndSetErrorFields } = useFormErrorFields({ form });

  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState(['']);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newSeller, setNewSeller] = useState<string | undefined>(undefined);
  const [newUserCampus, setNewUserCampus] = useState(['']);
  const [allowXlsxAnalysisReport, setAllowXlsxAnalysisReport] = useState<boolean>(false);
  const [allowCompanyBalanceConsumption, setAllowCompanyBalanceConsumption] = useState(true);

  const [disabledButtonFormNewUser, setDisabledButtonFormNewUser] = useState(true);
  const {
    campus: companyCampus,
    sellers,
    showCreateUserModal,
    isFormCreateLoading,
    hasEmailErrorCreate,
    filters
  } = useSelector((state: RootState) => state.users);
  const { company } = useSelector((state: RootState) => state.header);
  const { userStatus } = filters;

  const [keepOpenNewUserModal, setKeepOpenNewUserModal] = useState(false);

  const showBusinessManagerRole = (((isSuperAdmin() || isSalesRole() || isSalesManagerRole()) && company.isDistributor) || (isDistributorCompany() && isOwnerRole()));
  const hasPermissionToManageCompanies = isSuperAdmin() || isSalesRole() || isSalesManagerRole() || isPromoterRole() || isBusinessManagerRole();

  const onChangeRole = (value: string[]) => {
    if (newSeller && !value.includes(roles.PROMOTER)) {
      setNewSeller(undefined);
      form.setFieldsValue({
        seller: undefined,
        editSeller: undefined,
      });
    }
    setNewUserRole(value);
  };

  const resetCreateForm = () => {
    if (!keepOpenNewUserModal) {
      dispatch(usersSlice.setShowCreateUserModal(false));
    }

    setDisabledButtonFormNewUser(true);
    setAllowXlsxAnalysisReport(false);
    setAllowCompanyBalanceConsumption(true);
    form.resetFields();
  };

  const createUser = () => {
    const campusIds = newUserCampus.includes(ALL_UNITS) ? companyCampus.map((item: Campus) => item._id) : newUserCampus;

    const userData: CreateUser = {
      firstName: newUserFirstName,
      lastName: newUserLastName,
      email: newUserEmail,
      password: newUserPassword,
      roles: newUserRole,
      campus: campusIds,
      company: company,
      allowCompanyBalanceConsumption,
      allowXlsxAnalysisReport,
    };

    if (newUserRole.includes(roles.PROMOTER)) {
      userData.sellerId = newSeller;
    }

    const activeUsers = getValueActiveUsers(userStatus);
    dispatch(usersSlice.createUser(userData, activeUsers, resetCreateForm));
  };

  const renderFirstName = () => {
    const hasError = errorFields.includes('firstName');

    return (
      <Form.Item
        name='firstName'
        rules={[{ required: true, message: t('users.firstNameRequired') }]}
      >
        <LrvInputV2
          label={t('users.firstName')}
          theme='light'
          autoFocus
          value={newUserFirstName}
          hasError={hasError}
          onChange={(e) => setNewUserFirstName(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderLastName = () => {
    const hasError = errorFields.includes('lastName');

    return (
      <Form.Item
        name='lastName'
        rules={[{ required: true, message: t('users.lastNameRequired') }]}
      >
        <LrvInputV2
          label={t('users.lastName')}
          theme='light'
          value={newUserLastName}
          hasError={hasError}
          onChange={(e) => setNewUserLastName(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderEmail = () => {
    const hasError = errorFields.includes('email');

    return (
      <Form.Item
        name='email'
        validateStatus={hasEmailErrorCreate ? 'error' : undefined}
        help={hasEmailErrorCreate ? t('users.emailRepeat') : undefined}
        rules={[{ required: true, pattern: PATTERN.EMAIL, message: t('users.emailRequired') }]}
      >
        <LrvInputV2
          label={t('users.email')}
          theme='light'
          value={newUserEmail}
          hasError={hasError}
          onChange={(e) => setNewUserEmail(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderRole = () => {
    return (
      <Form.Item
        name='role'
        rules={[{ required: true, message: t('users.roleRequired') }]}
      >
        <LrvSelectV2
          label={t('users.role')}
          theme='light'
          mode='multiple'
          value={newUserRole}
          onChange={onChangeRole}
          removeIcon={<Icon name='close' />}
        >
          <Option value={roles.PARAMETERIST}>
            <div id='role_parameterist'>{t('users.parameterist')}</div>
          </Option>
          <Option value={roles.CLIENT_OPERATOR}>
            <div id='role_operator'>{t('users.clientOperator')}</div>
          </Option>
          <Option value={roles.CLIENT_MANAGER}>
            <div id='role_manager'>{t('users.clientManager')}</div>
          </Option>

          {
            (isSuperAdmin() || isSalesRole() || isSalesManagerRole()) && company._id === LARVIA_ID &&
        <Option value={roles.PROMOTER}>
          <div id='role_manager'>{t('users.clientPromoter')}</div>
        </Option>
          }

          {
            showBusinessManagerRole &&
        <Option value={roles.BUSINESS_MANAGER} >
          <div id='edit_role_business_manager'> {t('users.businessManager')} </div>
        </Option>
          }

        </LrvSelectV2>
      </Form.Item>
    );
  };
  const renderPromoter = () => {
    if (!newUserRole.includes(roles.PROMOTER)) {
      return;
    }

    return (
      <Form.Item
        name='seller'
        rules={[{ required: true, message: t('users.sellerRequired') }]}
      >
        <LrvSelectV2
          theme='light'
          value={newSeller}
          onChange={e => setNewSeller(e)}
          label={t('users.seller')}
        >
          {sellers.map((seller) =>
            <Option key={seller._id} value={seller._id}>
              {seller.firstName} {seller.lastName}
            </Option>)}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  const renderUnits = () => {
    return (
      <Form.Item
        name='campus'
        rules={[{ required: true, message: t('users.campusRequired') }]}
      >
        <LrvSelectV2
          label={t('users.campuses')}
          theme='light'
          mode='multiple'
          style={{ width: '100%' }}
          onChange={setNewUserCampus}
          removeIcon={<Icon name='close' />}
          showSearch
        >
          {companyCampus.length > 0 && <Option key={ALL_UNITS} value={ALL_UNITS}>{t('users.allUnits')}</Option>}
          {companyCampus && companyCampus.map((item: Campus, index) =>
            <Option key={index} value={item._id}>{item.name}</Option>
          )}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  const renderPassword = () => {
    const hasError = errorFields.includes('password');

    return (
      <Form.Item
        required
        name='password'
        rules={[() => ({ validator (rule, value) { return validatePasswordPolicy(value); } })]}
      >
        <LrvPasswordV2
          theme='light'
          label={
            <span>
              {t('users.password')} &nbsp;
              <PasswordPolicy />
            </span>
          }
          autoComplete='new-password'
          value={newUserPassword}
          hasError={hasError}
          onChange={(e) => setNewUserPassword(e.target.value)}
        />
      </Form.Item>
    );
  };

  return (
    <LrvModal
      theme='light'
      className='newUserModal'
      title={t('users.newUser')}
      open={showCreateUserModal}
      destroyOnClose={true}
      closeIcon={<Icon id='close_new_user_modal' name='close' />}
      footer={[
        <div key='left' className={styles.keepOpen}>
          <LrvCheckbox
            theme='light'
            checked={keepOpenNewUserModal}
            onChange={event => setKeepOpenNewUserModal(event.target.checked)}
          >
            {t('users.keepOpen')}
          </LrvCheckbox>
        </div>,

        <div key='right'>
          <Button
            key='cancel'
            id='cancel_new_user'
            onClick={() => {
              dispatch(usersSlice.setShowCreateUserModal(false));
              form.resetFields();
            }}
          >
            {t('users.cancel')}
          </Button>
          <Button
            type='primary'
            key='submit'
            id='submit_new_user'
            disabled={disabledButtonFormNewUser}
            htmlType='submit'
            form='formNewUser'
            loading={isFormCreateLoading}
            onClick={createUser}
          >
            {t('users.create')}
          </Button>
        </div>,
      ]}
      onCancel={() => {
        dispatch(usersSlice.setShowCreateUserModal(false));
        form.resetFields();
      }}
    >
      <LrvForm
        theme='light'
        form={form}
        name='formNewUser'
        id='formNewUser'
        layout='vertical'
        onFieldsChange={() => {
          const formFields = Object.keys(form.getFieldsValue());
          const fieldsNotRequired = ['allowCompanyBalanceConsumption', 'allowXlsxAnalysisReport'];
          const validFormFields = formFields.filter((element) => !fieldsNotRequired.includes(element));

          setDisabledButtonFormNewUser(
            !isFieldsTouchedCustom(form, validFormFields) || form.getFieldsError().filter(({ errors }) => errors.length).length > 0
          );

          extractAndSetErrorFields();
        }}
      >
        {renderFirstName()}
        {renderLastName()}
        {renderEmail()}
        {renderRole()}
        {renderPromoter()}
        {renderUnits()}
        {renderPassword()}

        {
          (company.allowXlsxAnalysisReport && hasPermissionToManageCompanies) &&
        <Form.Item
          name='allowXlsxAnalysisReport'
          label={t('users.allowXlsxAnalysisReport')}
        >
          <LrvSwitch
            theme='light'
            id='allow_xlsx_switch'
            checked={allowXlsxAnalysisReport}
            onChange={(value) => setAllowXlsxAnalysisReport(value)}
          />
        </Form.Item>
        }

        <Form.Item
          name='allowCompanyBalanceConsumption'
          label={t('users.allowCompanyBalanceConsumption')}
        >
          <LrvSwitch
            theme='light'
            id='allow_company_balance_consumption'
            checked={allowCompanyBalanceConsumption}
            onChange={(value) => setAllowCompanyBalanceConsumption(value)}
          />
        </Form.Item>
      </LrvForm>
    </LrvModal>
  );
};
